// AI Insights card — anomaly & expense hints powered by an LLM via Emergent Universal Key.
// Reads from /api/dealer/insights (12-hour server cache).

import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { Sparkles, RefreshCw, AlertTriangle, Lightbulb, TrendingUp, Info } from "lucide-react";

import { useTranslation } from "react-i18next";
const severityColor = (s) => {
  const sv = (s || "").toUpperCase();
  if (sv === "HIGH") return "text-rose-700 bg-rose-50 border-rose-200 dark:bg-rose-950/40 dark:text-rose-300 dark:border-rose-900";
  if (sv === "MED") return "text-amber-800 bg-amber-50 border-amber-200 dark:bg-amber-950/40 dark:text-amber-200 dark:border-amber-900";
  return "text-blue-800 bg-blue-50 border-blue-200 dark:bg-blue-950/30 dark:text-blue-200 dark:border-blue-900";
};

export default function AiInsightsCard() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [err, setErr] = useState(null);

  const load = useCallback(async (force = false) => {
    if (force) setRefreshing(true); else setLoading(true);
    setErr(null);
    try {
      const r = await api.get(`/dealer/insights${force ? "?refresh=1" : ""}`);
      setData(r.data);
    } catch (e) {
      setErr("Insights unavailable right now.");
    } finally { setLoading(false); setRefreshing(false); }
  }, []);

  useEffect(() => { load(false); }, [load]);

  return (
    <Card className="border-border" data-testid="ai-insights-card">
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <CardTitle className="flex items-center gap-1.5 text-base">
              <Sparkles className="h-4 w-4 text-primary" />
              AI Insights
              <span className="ml-1 rounded-full bg-secondary px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-muted-foreground">
                Beta
              </span>
            </CardTitle>
            <p className="text-xs text-muted-foreground">
              Anomaly flags + expense hints across your last 30 days
              {data?.generated_at && ` · updated ${new Date(data.generated_at).toLocaleString([], { dateStyle: "short", timeStyle: "short" })}`}
            </p>
          </div>
          <Button size="sm" variant="outline" onClick={() => load(true)} disabled={refreshing || loading} data-testid="ai-refresh">
            <RefreshCw className={"h-3.5 w-3.5 " + (refreshing ? "animate-spin" : "")} />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {loading && <div className="py-4 text-center text-xs text-muted-foreground">Analysing…</div>}
        {!loading && err && <div className="text-xs text-muted-foreground">{err}</div>}
        {!loading && data?.note === "insufficient_data" && (
          <div className="flex items-start gap-2 rounded-md border border-dashed border-border bg-secondary/40 p-3 text-xs text-muted-foreground" data-testid="ai-insufficient">
            <Info className="h-4 w-4 shrink-0" />
            <div>{t("components.aiinsightscard.submit_at_least_3_hisabs_so_i_can_start")}</div>
          </div>
        )}
        {!loading && data?.error && (
          <div className="rounded-md border border-amber-200 bg-amber-50 p-2 text-[11px] text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200" data-testid="ai-error">
            {data.error}
          </div>
        )}

        {!loading && (data?.top_insights || []).length > 0 && (
          <div data-testid="ai-top-insights">
            <div className="mb-1 flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              <TrendingUp className="h-3 w-3" /> Top insights
            </div>
            <ul className="space-y-1">
              {data.top_insights.map((s, i) => (
                <li key={i} className="rounded-md border border-border bg-secondary/40 p-2 text-xs text-foreground">
                  {s}
                </li>
              ))}
            </ul>
          </div>
        )}

        {!loading && (data?.anomaly_flags || []).length > 0 && (
          <div data-testid="ai-anomalies">
            <div className="mb-1 flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              <AlertTriangle className="h-3 w-3" /> Anomalies flagged
            </div>
            <div className="space-y-1">
              {data.anomaly_flags.map((a, i) => (
                <div key={i} className={"flex items-start gap-2 rounded-md border p-2 text-xs " + severityColor(a.severity)} data-testid={`ai-anomaly-${i}`}>
                  <span className="rounded-full bg-white/40 px-1.5 py-0.5 text-[9px] font-semibold uppercase">{a.kind}</span>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold">{a.date}{a.csa ? ` · ${a.csa}` : ""}</div>
                    <div>{a.detail}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {!loading && (data?.expense_hints || []).length > 0 && (
          <div data-testid="ai-expense-hints">
            <div className="mb-1 flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              <Lightbulb className="h-3 w-3" /> Expense reduction hints
            </div>
            <ul className="list-inside list-disc space-y-0.5 pl-1 text-xs text-foreground">
              {data.expense_hints.map((h, i) => <li key={i}>{h}</li>)}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
